import type { ISpeakerProfile, IStreamingSegment } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo, useState } from "react"
import { useAuth } from "../../../hooks/useAuth"
import { useCompanyApi } from "../../../hooks/useCompanyApi"
import { useCompanyMutation, useCompanyQuery } from "../../../hooks/useCompanyQuery"
import { useMeetingSpeakers } from "../../../hooks/useMeetingSpeakers"
import { useSpeakerHypothesisActions } from "../../../hooks/useSpeakerHypothesisActions"
import { useMeetingEvents } from "../../../hooks/useSSE"
import { fetchMeeting, fetchTranscript, patchTranscriptLine, restoreTranscript } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { notifyError } from "../../../lib/notify"
import { queryKeys } from "../../../lib/query-keys"
import { coerceSpeakerQuotes } from "../../../lib/speaker-mapping"
import type { ISpeakerVM, ISpeakerVMResult } from "../../../lib/speaker-vm"
import { enrichTranscriptLines } from "./helpers"
import type { IViewProps } from "./TranscriptView/interfaces"

type TFetchedMeeting = Awaited<ReturnType<typeof fetchMeeting>>

export interface IUseTranscriptPageOptions {
  /**
   * Attach `start` / `language` from `streaming_segments` onto each line.
   * Off by default. The transcript route and the Lite page pass true so each
   * line can show its offset from the start of the meeting.
   */
  enrichSegments?: boolean
}

export interface IUseTranscriptPageResult {
  viewProps: IViewProps
  speakerVMs: ISpeakerVMResult
  meeting: TFetchedMeeting | undefined
  editingSpeaker: ISpeakerVM | null
  editingProfile: ISpeakerProfile | undefined
  quotesMap: Map<string, string[]>
  speakerSegmentsMap: Map<string, IStreamingSegment[]>
  closeSpeakerEdit: () => void
  onSpeakerEdited: () => void
}

export function useTranscriptPage(
  meetingId: string,
  options: IUseTranscriptPageOptions = {},
): IUseTranscriptPageResult {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const [search, setSearch] = useState("")
  const [editingSpeaker, setEditingSpeaker] = useState<ISpeakerVM | null>(null)
  const queryClient = useQueryClient()
  const { i18n } = useLingui()
  const enrichSegments = options.enrichSegments === true

  const { data, isLoading, error, refetch } = useCompanyQuery({
    key: (org) => queryKeys.transcript(org, meetingId),
    queryFn: (client) => fetchTranscript(client, meetingId),
  })

  // Meeting detail — shares the meeting page's cache (same key + fetch), so a
  // navigation from the detail page is instant. Powers the unified speaker
  // view-model + the edit modal's auxiliary data. No polling: the transcript
  // page is never a live/finalize surface.
  const { data: meeting } = useCompanyQuery({
    key: (org) => queryKeys.meeting(org, meetingId),
    queryFn: (client) => fetchMeeting(client, meetingId),
  })

  const speakerVMs = useMeetingSpeakers(meeting)

  // The one confirm/reject owner for AI-name suggestions on the roster cards —
  // cross-invalidates every speaker surface on success (same hook the summary +
  // panel use), so a confirm here converges the detail page and list too.
  const hypothesis = useSpeakerHypothesisActions(meetingId)

  // Any speaker mutation anywhere (this page, the detail page, the summary
  // cards) broadcasts SPEAKERS_UPDATED; re-derive this page's roster + lines so
  // renames converge without a reload. The first callback (live phase changes)
  // is a no-op — the transcript page isn't a live surface.
  const noopTracked = useCallback((): void => {}, [])
  const onSpeakersUpdated = useCallback(
    (mid: string): void => {
      const safeId = mid.replace(/[^a-zA-Z0-9_-]/g, "_")
      if (safeId !== meetingId) return
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
    },
    [meetingId, orgId, queryClient],
  )
  useMeetingEvents(noopTracked, undefined, undefined, onSpeakersUpdated)

  const lines = useMemo(() => {
    if (!data) return []
    const source = enrichSegments ? enrichTranscriptLines(data.lines, meeting?.streaming_segments) : data.lines
    if (!search) return source
    const q = search.toLowerCase()
    return source.filter((l) => l.text.toLowerCase().includes(q) || l.speaker.toLowerCase().includes(q))
  }, [data, enrichSegments, meeting?.streaming_segments, search])

  // Per-speaker quotes + segments for the edit modal, mirroring how SpeakerPanel
  // feeds SpeakerCard: quotes keyed by resolved name, segments by raw label.
  const quotesMap = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const sq of coerceSpeakerQuotes(meeting?.analysis)) map.set(sq.speaker, sq.quotes)
    return map
  }, [meeting?.analysis])

  const speakerSegmentsMap = useMemo(() => {
    const map = new Map<string, IStreamingSegment[]>()
    for (const seg of meeting?.streaming_segments ?? []) {
      const bucket = map.get(seg.speaker)
      if (bucket) bucket.push(seg)
      else map.set(seg.speaker, [seg])
    }
    return map
  }, [meeting?.streaming_segments])

  // Reconstruct a profile for the edit card straight from the view-model (it
  // already carries colour / title / recognition / speaker_id). Undefined when
  // the speaker has no profile signal at all — matches SpeakerPanel passing
  // `profileMap.get()` for an unprofiled speaker.
  const editingProfile: ISpeakerProfile | undefined = useMemo(() => {
    if (!editingSpeaker) return undefined
    const { color, title, speakerId, recognitionSource } = editingSpeaker
    if (color === null && title === null && speakerId === null && recognitionSource === null) return undefined
    return {
      name: editingSpeaker.displayName,
      color,
      title,
      speaker_id: speakerId,
      recognition_source: recognitionSource,
    }
  }, [editingSpeaker])

  const saveMutation = useMutation({
    mutationFn: ({ index, oldText, newText }: { index: number; oldText: string; newText: string }) => {
      if (!company) return Promise.resolve({ success: false, suggested_terms: [] as string[] })
      return patchTranscriptLine(company, meetingId, index, oldText, newText)
    },
    onSuccess: (data) => {
      // The backend turns likely word corrections into pending jargon
      // suggestions (reviewed in the meeting sidebar / Jargon Manager).
      const suggested = data.suggested_terms ?? []
      if (suggested.length > 0) {
        notifications.show({
          title: i18n._(msg`Suggested for your dictionary`),
          message: suggested.join(", "),
          color: "info",
        })
        queryClient.invalidateQueries({ queryKey: queryKeys.jargonSuggestions(orgId, meetingId) })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
      refetch()
    },
    onError: (err: Error) => {
      // Keep the developer-facing detail out of the user's toast — show a
      // friendly recovery sentence and route the raw message through notifyError
      // so diagnostics still see the envelope.
      console.error("[transcript] line save failed", extractErrorMessage(err))
      notifyError(i18n._(msg`Your change couldn't be saved. Please try again.`), i18n._(msg`Save failed`))
    },
  })

  const savingIndex = saveMutation.isPending ? (saveMutation.variables?.index ?? null) : null

  const restoreMutation = useCompanyMutation({
    mutationFn: (client) => restoreTranscript(client, meetingId),
    onSuccess: () => {
      notifications.show({
        message: i18n._(msg`Original transcript restored.`),
        color: "success",
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
      refetch()
    },
    onError: (err: Error) => {
      console.error("[transcript] restore failed", extractErrorMessage(err))
      notifyError(
        i18n._(msg`The original transcript couldn't be restored. Please try again.`),
        i18n._(msg`Restore failed`),
      )
    },
  })

  const onSaveLine = useCallback(
    (index: number, oldText: string, newText: string): void => {
      saveMutation.mutate({ index, oldText, newText })
    },
    [saveMutation],
  )

  const onEditSpeaker = useCallback(
    (key: string): void => {
      const vm = speakerVMs.byLabel.get(key) ?? speakerVMs.byName.get(key)
      if (vm) setEditingSpeaker(vm)
    },
    [speakerVMs],
  )

  const closeSpeakerEdit = useCallback((): void => {
    setEditingSpeaker(null)
  }, [])

  const onSpeakerEdited = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
  }, [meetingId, orgId, queryClient])

  const viewProps: IViewProps = {
    meetingId,
    lines,
    speakers: speakerVMs.speakers,
    onEditSpeaker,
    onConfirmSuggestion: (label, name) => void hypothesis.onAction(label, name, "confirm"),
    onRejectSuggestion: (label, name) => void hypothesis.onAction(label, name, "reject"),
    suggestionPending: hypothesis.pending,
    speakerColorMap: speakerVMs.colorMap,
    search,
    onSearchChange: setSearch,
    isLoading,
    error: error ? extractErrorMessage(error) : null,
    savingIndex,
    onSaveLine,
    onRetry: () => refetch(),
    cleaned: data?.cleaned ?? false,
    restoring: restoreMutation.isPending,
    onRestore: () => restoreMutation.mutate(),
    lineCount: data?.lines.length,
  }

  return {
    viewProps,
    speakerVMs,
    meeting,
    editingSpeaker,
    editingProfile,
    quotesMap,
    speakerSegmentsMap,
    closeSpeakerEdit,
    onSpeakerEdited,
  }
}
